// Grid view — priority-lane kanban of project cards. Toggle between
// Priority and Department groupings. Backlogged projects are filtered out
// by the caller; we just render what we're given.

import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

// DEFAULT_DEPARTMENT_IDS no longer imported — defaults are seeded into
// RTDB on first run, so every department is equally deletable now.
import {
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_PRIORITY_ORDER,
} from '../../pm/constants';
import {
  createProjectDepartment,
  deleteProjectDepartment,
} from '../../pm/firebase';
import { useProjectDepartments } from '../../pm/useProjectDepartments';
import { AppUser, Project, ProjectPriority, Task } from '../../pm/types';
import ProjectCard, { departmentTag, priorityTag } from './ProjectCard';
import DepartmentColorPicker from './DepartmentColorPicker';
import EditableDepartmentLabel from './EditableDepartmentLabel';
import { IconClose, IconPlus } from './Icons';

type GroupBy = 'priority' | 'department';

interface Lane {
  id: string;
  label: string;
  dot: string;
  accent: string;
  projects: Project[];
}

interface DragProject {
  id: string;
  source: 'grid' | 'backlog' | 'done';
  sourcePriority?: ProjectPriority;
}

interface Props {
  /** uid of the current operator — used as createdBy on new departments. */
  uid: string;
  projects: Project[];
  tasks: Task[];
  admins: AppUser[];
  /** What's currently being dragged (null when idle). Drives lane drop
   *  outlines and the source-lane dimmed treatment. */
  dragProject: DragProject | null;
  /** Fired when the user starts dragging a card from this Grid. `priority`
   *  is the source lane so re-dropping on the same lane can no-op. */
  onProjectDragStart: (id: string, priority: ProjectPriority | undefined) => void;
  onProjectDragEnd: () => void;
  /** Drop into a lane — commits the new priority. Backlog-source drops
   *  also clear the backlogged flag (handled by the caller). */
  onDropOnLane: (target: ProjectPriority) => void;
}

const GridView: React.FC<Props> = ({
  uid,
  projects,
  tasks,
  admins,
  dragProject,
  onProjectDragStart,
  onProjectDragEnd,
  onDropOnLane,
}) => {
  const history = useHistory();
  const [groupBy, setGroupBy] = useState<GroupBy>('priority');
  const { departments } = useProjectDepartments();
  const [addingDept, setAddingDept] = useState(false);
  const [newDeptLabel, setNewDeptLabel] = useState('');

  // Map tasks → project bucket for O(1) lookups inside the card render path.
  const tasksByProject = useMemo(() => {
    const m: Record<string, Task[]> = {};
    for (const t of tasks) {
      if (!m[t.projectId]) m[t.projectId] = [];
      m[t.projectId].push(t);
    }
    return m;
  }, [tasks]);

  const lanes: Lane[] = useMemo(() => {
    if (groupBy === 'priority') {
      return PROJECT_PRIORITY_ORDER.map((id) => ({
        id,
        label: PROJECT_PRIORITY_LABELS[id],
        dot: PROJECT_PRIORITY_COLORS[id],
        accent: PROJECT_PRIORITY_COLORS[id],
        projects: projects.filter((p) => p.priority === id),
      }));
    }
    // Department mode pulls from the dynamic, RTDB-merged list so any
    // custom departments authored elsewhere immediately show up.
    return departments.map((d) => ({
      id: d.id,
      label: d.label,
      dot: d.swatch,
      accent: d.color,
      projects: projects.filter((p) => p.department === d.id),
    }));
  }, [projects, groupBy, departments]);

  // Note: projects without a priority/department don't render a fallback
  // "Unprioritized" lane in the Grid — they belong in the Backlog rail. The
  // caller already filters out `backlogged` projects, so an unprioritized
  // active project is the one case where it'd be invisible here; we count
  // on the Backlog drawer + ProjectDetail to surface those.

  // Accent color per card always tracks PRIORITY so the importance signal
  // is consistent regardless of grouping. (The design's preference.)
  function accentForCard(p: Project): string {
    if (p.priority) return PROJECT_PRIORITY_COLORS[p.priority];
    return 'var(--rr-stone-400)';
  }

  function secondaryFor(p: Project): ReturnType<typeof priorityTag> {
    return groupBy === 'priority' ? departmentTag(p.department, departments) : priorityTag(p.priority);
  }

  // Surface CRUD failures inline so the user gets feedback instead of
  // wondering whether nothing happened. The most common cause is the new
  // projectDepartments RTDB rule not being deployed yet — the error message
  // makes that legible without a full error screen.
  const [deptError, setDeptError] = useState<string | null>(null);

  async function handleAddDepartment(e?: React.FormEvent) {
    if (e) e.preventDefault();
    const label = newDeptLabel.trim();
    if (!label) return;
    setDeptError(null);
    try {
      await createProjectDepartment({ label }, uid);
      setNewDeptLabel('');
      setAddingDept(false);
    } catch (err: any) {
      // Most common failure: RTDB rules for projectDepartments haven't been
      // deployed. The Firebase error message includes "PERMISSION_DENIED"
      // which we surface verbatim so the cause is debuggable.
      setDeptError(err?.message || 'Could not add department.');
    }
  }
  async function handleDeleteDepartment(id: string, label: string) {
    if (!window.confirm(`Delete the "${label}" department?\n\nProjects assigned to it stay intact — they just lose the assignment.`)) return;
    setDeptError(null);
    try { await deleteProjectDepartment(id); }
    catch (err: any) { setDeptError(err?.message || 'Could not delete department.'); }
  }

  return (
    <div className="pm-grid">
      {/* Toolbar split into left (group control) and right (add-department).
       * The add control lives across from Group By so users can manage
       * departments without scrolling — matches the requested layout. */}
      <div className="pm-grid-toolbar">
        <div className="pm-grid-toolbar-left">
          <span className="pm-grid-toolbar-label">Group by</span>
          <div className="pm-grid-seg" role="tablist" aria-label="Grouping">
            <button
              type="button"
              role="tab"
              aria-selected={groupBy === 'priority'}
              className={groupBy === 'priority' ? 'is-on' : ''}
              onClick={() => setGroupBy('priority')}
            >
              Priority
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={groupBy === 'department'}
              className={groupBy === 'department' ? 'is-on' : ''}
              onClick={() => setGroupBy('department')}
            >
              Department
            </button>
          </div>
        </div>

        {/* Right slot — only meaningful in Department mode (priorities are
         * a fixed enum). When the user clicks "+ Add department" it
         * expands into a small inline form. */}
        {groupBy === 'department' ? (
          <div className="pm-grid-toolbar-right">
            {addingDept ? (
              <form onSubmit={handleAddDepartment} className="pm-grid-add-dept-form">
                <input
                  autoFocus
                  type="text"
                  value={newDeptLabel}
                  onChange={(e) => setNewDeptLabel(e.target.value)}
                  placeholder="Department name"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setAddingDept(false);
                      setNewDeptLabel('');
                      setDeptError(null);
                    }
                  }}
                />
                <button
                  type="submit"
                  className="pm-button pm-button-primary pm-button-sm"
                  disabled={!newDeptLabel.trim()}
                >
                  Add
                </button>
                <button
                  type="button"
                  className="pm-link-button"
                  onClick={() => {
                    setAddingDept(false);
                    setNewDeptLabel('');
                    setDeptError(null);
                  }}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="pm-grid-add-dept-trigger"
                onClick={() => setAddingDept(true)}
              >
                <IconPlus size={13} /> Add department
              </button>
            )}
          </div>
        ) : null}
      </div>

      {/* CRUD failure messages — the most common cause is the projectDepartments
       * RTDB rule not being deployed yet, so we surface the raw error to make
       * that debuggable. */}
      {deptError ? <div className="pm-error pm-grid-dept-error">{deptError}</div> : null}

      <div
        className="pm-grid-lanes"
        style={{ gridTemplateColumns: `repeat(${lanes.length}, minmax(0, 1fr))` }}
      >
        {lanes.map((lane) => {
          // Lane drop targeting is only meaningful when we're grouping by
          // priority (we update the project's priority field on drop). In
          // Department grouping the lanes don't map cleanly to a writeable
          // field for project rearrangement, so we treat those lanes as
          // visual-only and skip the drop wiring.
          const isPriorityLane = groupBy === 'priority';
          const lanePriority = isPriorityLane ? (lane.id as ProjectPriority) : undefined;
          const isSource =
            !!dragProject &&
            dragProject.source === 'grid' &&
            dragProject.sourcePriority === lanePriority;
          const accepts = !!dragProject && isPriorityLane && !isSource;
          // Lane gets a tagged className during a drag so CSS can render the
          // drop outline. We track per-lane via state-less className flip:
          // `accepts && hovered` would need state; for now we always show
          // the dashed outline on accept-eligible lanes, which is a clear
          // affordance even without per-lane hover tracking.
          const laneClasses = [
            'pm-grid-lane',
            accepts ? 'is-drop-target' : '',
            isSource ? 'is-drop-source' : '',
          ]
            .filter(Boolean)
            .join(' ');

          const dropHandlers = isPriorityLane
            ? {
                onDragOver: (e: React.DragEvent) => {
                  // Only intercept if a project drag is active; otherwise let
                  // the event bubble so we don't hijack random browser drops.
                  if (!dragProject) return;
                  e.preventDefault();
                  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                },
                onDrop: (e: React.DragEvent) => {
                  if (!dragProject || !lanePriority) return;
                  e.preventDefault();
                  onDropOnLane(lanePriority);
                },
              }
            : {};

          // In Department mode every lane gets a delete affordance. The
          // built-in four (audit/implementation/strategic/brand) are
          // seeded into RTDB on first run, so they live in the same
          // collection as customs and are equally deletable.
          const showDeleteX = groupBy === 'department';

          return (
            <div key={lane.id} className={laneClasses}>
              <div className="pm-grid-lane-hd">
                {/* In Department mode the dot doubles as a color picker;
                 * priority lanes use a static dot (priority colors are
                 * fixed via PROJECT_PRIORITY_COLORS). */}
                {groupBy === 'department' ? (
                  <DepartmentColorPicker departmentId={lane.id} swatch={lane.dot} />
                ) : (
                  <span className="pm-pri-dot" style={{ background: lane.dot }} />
                )}
                {/* Department labels are click-to-edit; priority labels
                 * come from PROJECT_PRIORITY_LABELS and aren't user-editable. */}
                {groupBy === 'department' ? (
                  <EditableDepartmentLabel departmentId={lane.id} label={lane.label} />
                ) : (
                  <span className="pm-grid-lane-label">{lane.label}</span>
                )}
                <span className="pm-grid-lane-count">{lane.projects.length}</span>
                {showDeleteX ? (
                  <button
                    type="button"
                    className="pm-grid-lane-remove"
                    title={`Remove "${lane.label}"`}
                    aria-label={`Remove department ${lane.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDepartment(lane.id, lane.label);
                    }}
                  >
                    <IconClose size={10} />
                  </button>
                ) : null}
              </div>
              <div className="pm-grid-lane-body" {...dropHandlers}>
                {lane.projects.length === 0 ? (
                  <div className="pm-grid-lane-empty">No projects</div>
                ) : (
                  lane.projects.map((p) => {
                    const isCardDragging = dragProject?.id === p.id;
                    return (
                      <div
                        key={p.id}
                        // Native HTML5 drag — `draggable` on the wrapping div
                        // so all the existing ProjectCard chrome (click,
                        // keyboard) stays intact. The card itself remains a
                        // role=button; pointer drag starts here.
                        draggable={isPriorityLane}
                        onDragStart={(e) => {
                          if (!isPriorityLane) return;
                          // Browsers want SOME payload to enable drag; the
                          // real source-of-truth lives in React state via
                          // onProjectDragStart, so the dataTransfer content
                          // is just a marker.
                          if (e.dataTransfer) {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', p.id);
                          }
                          onProjectDragStart(p.id, lanePriority);
                        }}
                        onDragEnd={onProjectDragEnd}
                        className={`pm-proj-card-wrap${isCardDragging ? ' pm-proj-card-wrap-dragging' : ''}`}
                      >
                        <ProjectCard
                          project={p}
                          tasks={tasksByProject[p.id] || []}
                          admins={admins}
                          accentColor={accentForCard(p)}
                          secondaryTag={secondaryFor(p)}
                          onOpen={() => history.push(`/Projects/${p.id}`)}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add-department UI moved to the top-right of the toolbar; nothing
       * lives below the lanes any more. */}

    </div>
  );
};

export default GridView;

// Helper used by the parent so the dot/label can also be reused elsewhere.
export function laneAccentForPriority(p: ProjectPriority): string {
  return PROJECT_PRIORITY_COLORS[p];
}
// laneAccentForDepartment removed — no external callers, and the
// dynamic department list now lives behind useProjectDepartments().
